import { useState, useEffect } from "react";
import { Send, Mail, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AnimateOnScroll, SectionLabel, SectionHeading, ScrollToTop } from "@/components/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  ScrollToTop();
  const [info, setInfo] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${API}/content/contact`).then(r => r.json()).then(setInfo).catch(() => {});
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
      setFormData({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
    }
  };

  const email = info?.email || "contact.georgeemad@gmail.com";
  const socialLinks = info?.socialLinks || [];

  return (
    <main data-testid="contact-page" className="pt-24 md:pt-28 pb-24 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimateOnScroll><SectionLabel text="Contact" /><SectionHeading>Let's Collaborate</SectionHeading>
          <p className="mt-4 text-zinc-400 max-w-lg">Interested in a brand collaboration, sponsorship, or creative project? Get in touch.</p></AnimateOnScroll>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <AnimateOnScroll className="lg:col-span-2">
            <div className="space-y-8">
              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="p-3 border border-white/10 text-zinc-500"><Mail size={18} /></div>
                <div><h4 className="font-heading text-sm font-semibold text-white">Email</h4>
                  <a href={`mailto:${email}`} data-testid="contact-email-link" className="mt-1 text-sm text-zinc-400 hover:text-[#E63946] transition-colors">{email}</a></div>
              </div>

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="pt-6 border-t border-white/5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 block mb-4">Social Profiles</span>
                  <div className="flex gap-3">
                    {socialLinks.map((s) => (
                      <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-zinc-500 hover:text-[#E63946] transition-colors flex items-center gap-1">
                        {s.label}<ArrowUpRight size={10} /></a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll delay={0.15} className="lg:col-span-3">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="p-6 md:p-8 bg-[#0F0F11] border border-white/5 space-y-6">
              <h3 className="font-heading text-lg font-semibold text-white">Brand Collaboration Inquiry</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Name</label>
                  <Input name="name" value={formData.name} onChange={handleChange} required data-testid="contact-input-name" placeholder="Your name" className="bg-[#050505] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#E63946] focus:ring-[#E63946]" /></div>
                <div><label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Email</label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} required data-testid="contact-input-email" placeholder="you@company.com" className="bg-[#050505] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#E63946] focus:ring-[#E63946]" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Company</label>
                  <Input name="company" value={formData.company} onChange={handleChange} data-testid="contact-input-company" placeholder="Your company" className="bg-[#050505] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#E63946] focus:ring-[#E63946]" /></div>
                <div><label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Subject</label>
                  <Input name="subject" value={formData.subject} onChange={handleChange} required data-testid="contact-input-subject" placeholder="Collaboration type" className="bg-[#050505] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#E63946] focus:ring-[#E63946]" /></div>
              </div>
              <div><label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Message</label>
                <Textarea name="message" value={formData.message} onChange={handleChange} required data-testid="contact-input-message" placeholder="Tell me about your project..." rows={5} className="bg-[#050505] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#E63946] focus:ring-[#E63946] resize-none" /></div>
              <Button type="submit" data-testid="contact-submit-button" className="w-full bg-[#E63946] text-white font-semibold tracking-wide hover:bg-[#F25C69] transition-colors duration-200 py-6">
                {submitted ? "Message Sent Successfully" : <span className="flex items-center gap-2"><Send size={16} />Send Message</span>}
              </Button>
            </form>
          </AnimateOnScroll>
        </div>
      </div>
    </main>
  );
}
